import React from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import SEO, { breadcrumbSchema } from "@/components/SEO";

export default function Services() {
  return (
    <div data-testid="page-services" className="pt-32">
      <SEO
        title="Services — Luxury Consulting & Training"
        description="Two disciplines by Next in Luxury: Luxury Consulting — strategic advisory, brand strategy and customer experience — and Luxury Training — executive programmes, service excellence and savoir-faire transmission."
        path="/services"
        schema={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Next in Luxury — Services",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                url: "https://www.nextinluxury.com/services/consulting",
                name: "Luxury Consulting",
              },
              {
                "@type": "ListItem",
                position: 2,
                url: "https://www.nextinluxury.com/services/training",
                name: "Luxury Training",
              },
            ],
          },
        ]}
      />
      {/* Title */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-16 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-2">
          <p className="font-eyebrow opacity-60">Chapter</p>
        </div>
        <div className="col-span-12 md:col-span-10">
          <Reveal>
            <p className="font-eyebrow" style={{ color: "#8C6B3F" }}>
              — Two Disciplines
            </p>
            <h1
              className="title-caps leading-[0.9] mt-8"
              style={{ fontSize: "clamp(3rem, 9vw, 9rem)" }}
            >
              THE
              <br />
              <span className="font-script" style={{ textTransform: "none", letterSpacing: 0, fontSize: "1.2em", color: "#8C6B3F" }}>
                Services.
              </span>
            </h1>
            <p className="font-body opacity-85 mt-12 max-w-2xl text-lg leading-relaxed">
              We work in two disciplines — Consulting and Training — the first
              defines direction, the second transmits savoir-faire. Together
              they shape the culture, capability and discipline of an exceptional
              brand.
            </p>
          </Reveal>
        </div>
      </section>

      <div className="hairline max-w-[1440px] mx-auto my-16" />

      {/* Two-panel service cards */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 pb-24 grid grid-cols-1 md:grid-cols-2 gap-8">
        {[
          {
            slug: "consulting",
            n: "I",
            eyebrow: "I. Advisory",
            caps: "CONSULTING",
            script: "Strategy that becomes reality.",
            dek: "We define direction and embed it into the daily reality of the business. Strategy that survives on the floor, not just on the slide.",
            image: "https://images.squarespace-cdn.com/content/v1/68f648f746888b590facb861/4fd57818-473e-4202-a808-a8c5dff24f09/Screenshot+2026-07-02+at+12.33.46%E2%80%AFam.png?format=2000w",
          },
          {
            slug: "training",
            n: "II",
            eyebrow: "II. Transmission",
            caps: "TRAINING",
            script: "Savoir-faire that outlasts.",
            dek: "We transmit the culture, capabilities and human intelligence to become the next reference. Programmes designed for leaders, teams and institutions.",
            image: "https://images.squarespace-cdn.com/content/v1/68f648f746888b590facb861/b61cb60c-2a0a-4058-aebc-1f3eab280158/Screenshot+2026-07-02+at+12.47.57%E2%80%AFam.png?format=2000w",
          },
        ].map((s, i) => (
          <Reveal key={s.slug} delay={i * 120}>
            <Link
              to={`/services/${s.slug}`}
              data-testid={`services-card-${s.slug}`}
              className="group block"
            >
              <div className="overflow-hidden bg-[#0A0A08]">
                <img
                  src={s.image}
                  alt={s.caps}
                  className="w-full aspect-[4/5] object-cover transition-transform duration-[1400ms] group-hover:scale-[1.03]"
                  style={{ filter: "grayscale(1) contrast(1.15) brightness(0.9) saturate(0)" }}
                />
              </div>
              <div className="mt-6 flex justify-between items-baseline">
                <p className="font-eyebrow" style={{ color: "#8C6B3F" }}>
                  {s.eyebrow}
                </p>
                <p className="font-eyebrow opacity-40 text-[10px]">Chapter N°{s.n}</p>
              </div>
              <h3
                className="title-caps mt-6 leading-[0.95]"
                style={{ fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}
              >
                {s.caps}
              </h3>
              <p className="font-script mt-2" style={{ color: "#8C6B3F", fontSize: "clamp(2.25rem, 4vw, 3.5rem)", lineHeight: 0.85 }}>
                {s.script}
              </p>
              <p className="font-body opacity-75 mt-6 max-w-lg leading-relaxed">{s.dek}</p>
              <span className="btn-ghost mt-8 inline-flex">
                Discover {s.caps} <span className="arrow">→</span>
              </span>
            </Link>
          </Reveal>
        ))}
      </section>
    </div>
  );
}
